import type { EnvironmentId, ProjectId } from "@cmmd-forge/contracts";

import type {
  NoActiveThreadCommandTaskRow,
  NoActiveThreadCommandTaskSnapshotInput,
  NoActiveThreadRecentItem,
  NoActiveThreadSnapshot,
  NoActiveThreadSnapshotInput,
} from "./NoActiveThreadState.logic";
import { projectDisplayName } from "./projects/projectDisplayName";

const DEFAULT_RECENT_THREAD_LIMIT = 4;
const DEFAULT_COMMAND_TASK_LIMIT = 6;

function toProjectMapKey(environmentId: EnvironmentId, projectId: ProjectId): string {
  return `${environmentId}:${projectId}`;
}

function toActivityTimestamp(thread: NoActiveThreadSnapshotInput["threads"][number]): number {
  const preferred = thread.latestUserMessageAt ?? thread.updatedAt ?? thread.createdAt;
  const parsed = Date.parse(preferred);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toActivityAt(thread: NoActiveThreadSnapshotInput["threads"][number]): string {
  return thread.latestUserMessageAt ?? thread.updatedAt ?? thread.createdAt;
}

export function buildNoActiveThreadSnapshot(
  input: NoActiveThreadSnapshotInput,
): NoActiveThreadSnapshot {
  const projectNameByScopedId = new Map<string, string>();
  for (const project of input.projects) {
    projectNameByScopedId.set(toProjectMapKey(project.environmentId, project.id), project.name);
  }

  const recentThreads = input.threads
    .toSorted((left, right) => toActivityTimestamp(right) - toActivityTimestamp(left))
    .slice(0, input.maxRecentThreads ?? DEFAULT_RECENT_THREAD_LIMIT)
    .map((thread) => {
      const projectName =
        projectNameByScopedId.get(toProjectMapKey(thread.environmentId, thread.projectId)) ??
        "Unknown project";

      return {
        id: thread.id,
        environmentId: thread.environmentId,
        projectId: thread.projectId,
        title: thread.title,
        projectName,
        activityAt: toActivityAt(thread),
      } satisfies NoActiveThreadRecentItem;
    });

  return {
    projectCount: input.projects.length,
    threadCount: input.threads.length,
    recentThreads,
  };
}

function parseTimestamp(value: string | null): number {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function isCompletedTaskStatus(status: string | null): boolean {
  const normalized = status?.trim().toLowerCase();
  if (!normalized) return false;
  return ["done", "completed", "closed", "complete", "resolved"].includes(normalized);
}

function resolveTaskPriorityWeight(priority: string | null): number {
  const normalized = priority?.trim().toLowerCase();
  if (!normalized) return 1;
  if (["critical", "urgent"].includes(normalized)) return 5;
  if (["high", "p1"].includes(normalized)) return 4;
  if (["medium", "normal", "p2"].includes(normalized)) return 3;
  if (["low", "minor", "p3"].includes(normalized)) return 2;
  return 1;
}

function compareCompletionStatus(
  left: NoActiveThreadCommandTaskSnapshotInput["tasks"][number],
  right: NoActiveThreadCommandTaskSnapshotInput["tasks"][number],
): number {
  const leftComplete = isCompletedTaskStatus(left.status);
  const rightComplete = isCompletedTaskStatus(right.status);
  if (leftComplete === rightComplete) return 0;
  return leftComplete ? 1 : -1;
}

function comparePriority(
  left: NoActiveThreadCommandTaskSnapshotInput["tasks"][number],
  right: NoActiveThreadCommandTaskSnapshotInput["tasks"][number],
): number {
  return resolveTaskPriorityWeight(right.priority) - resolveTaskPriorityWeight(left.priority);
}

function compareDueDate(
  left: NoActiveThreadCommandTaskSnapshotInput["tasks"][number],
  right: NoActiveThreadCommandTaskSnapshotInput["tasks"][number],
): number {
  const leftDue = parseTimestamp(left.dueDate);
  const rightDue = parseTimestamp(right.dueDate);
  if (leftDue === 0 && rightDue === 0) return 0;
  if (leftDue === 0) return 1;
  if (rightDue === 0) return -1;
  return leftDue - rightDue;
}

function compareUpdatedAt(
  left: NoActiveThreadCommandTaskSnapshotInput["tasks"][number],
  right: NoActiveThreadCommandTaskSnapshotInput["tasks"][number],
): number {
  return parseTimestamp(right.updatedAt) - parseTimestamp(left.updatedAt);
}

function compareCommandTaskRows(
  left: NoActiveThreadCommandTaskSnapshotInput["tasks"][number],
  right: NoActiveThreadCommandTaskSnapshotInput["tasks"][number],
): number {
  return (
    compareCompletionStatus(left, right) ||
    comparePriority(left, right) ||
    compareDueDate(left, right) ||
    compareUpdatedAt(left, right)
  );
}

type CommandTaskProjectLookup = ReadonlyMap<
  string,
  {
    readonly title: string;
    readonly shortName: string | null;
    readonly campaignName: string | null;
    readonly workspaceId: string | null;
    readonly workspaceName: string | null;
    readonly workspaceIconUrl: string | null;
    readonly icon: string | null;
    readonly iconUrl: string | null;
    readonly teamId: string | null;
    readonly teamName: string | null;
  }
>;
type CommandTaskProject =
  CommandTaskProjectLookup extends ReadonlyMap<string, infer Value> ? Value : never;

function buildCommandTaskRow(input: {
  readonly task: NoActiveThreadCommandTaskSnapshotInput["tasks"][number];
  readonly projectById: CommandTaskProjectLookup;
}): NoActiveThreadCommandTaskRow {
  const project = resolveCommandTaskProject(input);
  return {
    id: input.task.id,
    title: input.task.title,
    status: input.task.status,
    priority: input.task.priority,
    projectId: input.task.projectId,
    ...resolveCommandTaskProjectFields(project),
    ...resolveCommandTaskWorkspaceFields(input.task, project),
    ...resolveCommandTaskTeamFields(input.task, project),
    assigneeId: input.task.assigneeId ?? null,
    dueDate: input.task.dueDate,
    updatedAt: input.task.updatedAt,
    dependsOn: input.task.dependsOn,
  };
}

function resolveCommandTaskProject(input: {
  readonly task: NoActiveThreadCommandTaskSnapshotInput["tasks"][number];
  readonly projectById: CommandTaskProjectLookup;
}): CommandTaskProject | null {
  if (!input.task.projectId) return null;
  return input.projectById.get(input.task.projectId) ?? null;
}

function resolveCommandTaskProjectFields(
  project: CommandTaskProject | null,
): Pick<
  NoActiveThreadCommandTaskRow,
  "projectName" | "projectIcon" | "projectIconUrl" | "campaignName"
> {
  return {
    projectName: projectDisplayName(project) ?? "Unlinked project",
    projectIcon: project?.icon ?? null,
    projectIconUrl: project?.iconUrl ?? null,
    campaignName: project?.campaignName ?? null,
  };
}

function resolveCommandTaskWorkspaceFields(
  task: NoActiveThreadCommandTaskSnapshotInput["tasks"][number],
  project: CommandTaskProject | null,
): Pick<NoActiveThreadCommandTaskRow, "workspaceId" | "workspaceName" | "workspaceIconUrl"> {
  return {
    workspaceId: task.workspaceId ?? project?.workspaceId ?? null,
    workspaceName: task.workspaceName ?? project?.workspaceName ?? null,
    workspaceIconUrl: task.workspaceIconUrl ?? project?.workspaceIconUrl ?? null,
  };
}

function resolveCommandTaskTeamFields(
  task: NoActiveThreadCommandTaskSnapshotInput["tasks"][number],
  project: CommandTaskProject | null,
): Pick<NoActiveThreadCommandTaskRow, "teamId" | "teamName"> {
  return {
    teamId: task.teamId ?? project?.teamId ?? null,
    teamName: task.teamName ?? project?.teamName ?? null,
  };
}

export function buildNoActiveThreadCommandTaskRows(
  input: NoActiveThreadCommandTaskSnapshotInput,
): ReadonlyArray<NoActiveThreadCommandTaskRow> {
  const campaignById = new Map<string, string>();
  for (const campaign of input.campaigns) {
    campaignById.set(campaign.id, campaign.title);
  }

  const projectById = new Map<
    string,
    {
      readonly title: string;
      readonly shortName: string | null;
      readonly campaignName: string | null;
      readonly workspaceId: string | null;
      readonly workspaceName: string | null;
      readonly workspaceIconUrl: string | null;
      readonly icon: string | null;
      readonly iconUrl: string | null;
      readonly teamId: string | null;
      readonly teamName: string | null;
    }
  >();
  for (const project of input.projects) {
    projectById.set(project.id, {
      title: project.title,
      shortName: project.shortName ?? null,
      campaignName: project.campaignId ? (campaignById.get(project.campaignId) ?? null) : null,
      workspaceId: project.workspaceId ?? null,
      workspaceName: project.workspaceName ?? null,
      workspaceIconUrl: project.workspaceIconUrl ?? null,
      icon: project.icon ?? null,
      iconUrl: project.iconUrl ?? null,
      teamId: project.teamId ?? null,
      teamName: project.teamName ?? null,
    });
  }

  return input.tasks
    .toSorted(compareCommandTaskRows)
    .slice(0, input.maxTasks ?? DEFAULT_COMMAND_TASK_LIMIT)
    .map((task) => buildCommandTaskRow({ task, projectById }));
}
